import {Injectable} from '@angular/core';
import {AttributeTableRecord} from '../../interfaces/attribute-table';
import {BehaviorSubject} from 'rxjs';
import {IdentifyParseService} from '../identify/identify-parse.service';
import {QueryObject, QueryObjectResults} from '../../interfaces/query';
import {QueryService} from '../query/query.service';
import {GoogleAnalyticsService} from '../googleAnalytics/google-analytics.service';

@Injectable({
  providedIn: 'root'
})
export class AttributeService {
  private attributeObservable = new BehaviorSubject(null);
  public tableObj: AttributeTableRecord[] = [];

  constructor(private identify: IdentifyParseService, private query: QueryService, private ga: GoogleAnalyticsService) { }

  public getAttributeObservable() { return this.attributeObservable.asObservable(); }
  public getTableObj() { return this.tableObj; }
  public removeTab(index) { this.tableObj.splice(index, 1); }
  public removeAll() { this.tableObj = []; }

  /**
   * Handles what happens when a query is made or an attribute table layer is opened
   * @param result: Initial ArcGIS Server REST query result
   */
  public async onQuery(result) {
    try {
      const fullQuery = this.mergeQueryArray(result);
      let columns = this.getColumns(fullQuery);
      columns = this.setupColumnDef(columns);
      let rows = this.getRows(fullQuery);
      const tableObj: AttributeTableRecord = this.createTableObject(fullQuery, columns, rows);
      this.tableObj.unshift(tableObj);
      this.attributeObservable.next(true);

      if (fullQuery.queryTaskObj.relateObj) {
        const queryRelates = await this.queryRelates(fullQuery);
        const fullRelatedQuery = this.mergeQueryRelatedArray(queryRelates);
        for (const layer of fullRelatedQuery) {
          columns = this.getColumns(layer);
          columns.unshift({headerName: 'ObjectID of relate', field: 'relatedObjectID'});
          columns = this.setupColumnDef(columns);
          rows = this.getRelatedRows(layer);
          const relateObj: AttributeTableRecord = this.createTableObject(layer, columns, rows);
          this.tableObj.unshift(relateObj);
          this.attributeObservable.next(true);
        }
      }
      return true;
    } catch (error) {
      this.ga.exception(error, false);
      return false;
    }
  }

  /**
   * Sets up the default column definitions for the first row
   * It allows the first row to have a checkbox on each item, and a checkbox on the column itself
   * @param columns: Array of columns for ag-grid display
   */
  private setupColumnDef(columns) {
    if (columns.length > 0) {
      columns[0].checkboxSelection = true;
      columns[0].headerCheckboxSelection = true;
      columns[0].headerCheckboxSelectionFilteredOnly = true;
    }
    return columns;
  }

  /**
   * Creates a attribute table object for display in the attribute table component
   * @param layer: AGSLayer object
   * @param columns: Formatted columns to be displayed on the ag-grid table
   * @param rows: Formatted rows to be displayed on the ag-grid table
   */
  private createTableObject(layer, columns, rows): AttributeTableRecord {
    return  {
      alias: layer.queryTaskObj.name,
      columns,
      count: layer.queryResults.featureCount,
      color: null,
      header: layer.queryTaskObj.name,
      parent: layer.queryTaskObj.parent,
      outFields: layer.queryTaskObj.outFields,
      relateObj: layer.queryTaskObj.relateObj,
      rows,
      url: layer.queryTaskObj.layerTaskUrl,
      formatFields: layer.queryTaskObj.formatFields,
      token: layer.queryTaskObj.token
    };
  }

  /**
   * Merges an array of ArcGIS Server REST query results for a single service into a single object
   * @param result: ArcGIS Server REST query result
   */
  private mergeQueryArray(result) {
    const fullQuery = result[0];
    let index = 0;
    for (const layer of result) {
      if (index !== 0) {
        fullQuery.queryResults.featureSet.features =
          fullQuery.queryResults.featureSet.features.concat(layer.queryResults.featureSet.features);
        fullQuery.queryResults.featureCount += layer.queryResults.featureCount;
        fullQuery.queryTaskObj.objectIds += layer.queryTaskObj.objectIds;
      }
      index++;
    }
    return fullQuery;
  }

  /**
   * Merges an array of ArcGIS Server REST query results from all of the related tables of a service
   * This will merge each table into it's own array entry
   * @param result: Array of ArcGIS Server related table REST queries
   */
  private mergeQueryRelatedArray(result) {
    const fullQuery = [];
    if (result[0]) {
      fullQuery.push(result[0]);
      for (const layer of result) {
        let layerExists = false;
        for (const mergedLayer of fullQuery) {
          if (mergedLayer.queryTaskObj.name === layer.queryTaskObj.name &&
            mergedLayer.queryResults.featureSet.relatedRecordGroups.length > 0) {
            layerExists = true;
            mergedLayer.queryResults.featureSet.relatedRecordGroups =
              mergedLayer.queryResults.featureSet.relatedRecordGroups.concat(layer.queryResults.featureSet.relatedRecordGroups);
            mergedLayer.queryTaskObj.objectIds += layer.queryTaskObj.objectIds;
          }
        }
        if (!layerExists) {
          fullQuery.push(layer);
        }
      }
    }
    return fullQuery;
  }

  /**
   * Formats the row object from an ArcGIS REST query into an array of rows for the ag-grid API
   * @param serverQuery: ArcGIS REST query result
   */
  private getRows(serverQuery) {
    const rows = [];
    if (serverQuery && serverQuery.queryResults && serverQuery.queryResults.featureSet) {
      for (const record of serverQuery.queryResults.featureSet.features) {
        let item = Object.assign({}, record.attributes);
        item = this.identify.formatCheck(serverQuery.queryResults.featureSet.fields, item, serverQuery.queryTaskObj.formatFields);
        item.geometry = record.geometry;
        item.geometryType = serverQuery.queryResults.featureSet.geometryType;
        rows.push(item);
      }
    }
    return rows;
  }

  /**
   * Formats the row object from a related table ArcGIS REST query into an array of rows for the ag-grid API
   * @param serverQuery: ArcGIS REST query result
   */
  private getRelatedRows(serverQuery) {
    const rows = [];
    for (const records of serverQuery.queryResults.featureSet.relatedRecordGroups) {
      for (const record of records.relatedRecords) {
        let item = Object.assign({}, record.attributes);
        item = this.identify.formatCheck(serverQuery.queryResults.featureSet.fields, item, serverQuery.queryTaskObj.formatFields);
        item.relatedObjectID = records.objectId;
        rows.push(item);
      }
    }
    return rows;
  }

  /**
   * Parses query result for column object
   * Builds the column object required by ag-grid to function correctly
   */
  public getColumns(serverQuery) {
    const column = [];
    if (serverQuery && serverQuery.queryResults && serverQuery.queryResults.featureSet && serverQuery.queryResults.featureSet.fields) {
      for (const row of serverQuery.queryResults.featureSet.fields) {
        const entry: any = {headerName: row.alias, field: row.name};
        if (row.alias.toLowerCase().search('url') > -1 || row.alias.toLowerCase().search('link') > -1) {
          entry.cellRenderer = (params) => {
            if (params.value) { return '<a href="' + params.value + '" target="_blank">Link</a>'; } else { return null; }
          };
        }
        if (row.alias.toLowerCase().search('date') > -1) {
          entry.comparator = this.sortDate;
        }
        column.push(entry);
      }
    }
    return column;
  }

  /**
   * Date sorting function
   * @param date1: first date to sort
   * @param date2: second date to sort
   */
  private sortDate(date1, date2) {
    if (date1 && date2) {
      date1 = new Date(date1);
      date2 = new Date(date2);
      return date2 - date1;
    } else if (date1 === null) {
      return -1;
    } else if (date2 === null) {
      return 1;
    } else { return 0; }
  }

  /**
   * Takes an ArcGIS REST query and then queries all of it's related tables
   * Will query 1000 records at a time for all of the related tables
   * Returns an array of promises with all of the relates
   * @query: ArcGIS REST query
   */
  private async queryRelates(query) {
    try {
      const queryArr = [];
      if (query.queryTaskObj.relateObj && query.queryTaskObj.objectIds) {
        for (const j of query.queryTaskObj.relateObj) {
          const objectIdArr = query.queryTaskObj.objectIds.split(',');
          for (let i = 0; i < objectIdArr.length; i += 1000) {
            let subset = [];
            subset = objectIdArr.slice(i, i + 1000);
            let objectids = '';
            for (const chunk of subset) {
              if (objectids === '') {
                objectids = objectids + chunk;
              } else {
                objectids = objectids + ',' + chunk;
              }
            }
            const relateQueryTaskObj: QueryObject = {};
            relateQueryTaskObj.objectIds = objectids;
            relateQueryTaskObj.layerTaskUrl = query.queryTaskObj.layerTaskUrl;
            relateQueryTaskObj.name = query.queryTaskObj.name + ': ' + j.alias;
            relateQueryTaskObj.parent = query.queryTaskObj.name;
            relateQueryTaskObj.outFields = j.outFields;
            relateQueryTaskObj.formatFields = j.formatFields;
            relateQueryTaskObj.relationshipClassIDS = j.ID;
            relateQueryTaskObj.queryMethod = 'POST';
            relateQueryTaskObj.returnGeometry = true;
            relateQueryTaskObj.token = query.queryTaskObj.token;
            relateQueryTaskObj.relateObj = query.queryTaskObj.relateObj;
            queryArr.push(this.query.queryData(relateQueryTaskObj));
          }
        }
      }
      return await Promise.all(queryArr);
    } catch (error) {
      this.ga.exception(error, false);
      return [];
    }
  }

  /**
   * Returns all of the features in a requested AGSLayer
   * @param queryObj: ArcGIS Server REST query
   */
  public  async getFullLayer(queryObj: QueryObject): Promise<boolean> {
    try {
      const result = await this.query.queryData(queryObj);
      const queryAr = await this.parseQuery(result);
      this.onQuery(queryAr);
      return true;
    } catch (error) {
      this.ga.exception(error, false);
      return false;
    }
  }

  /**
   * Handles the event fired when a user clicks on a layer in the select visible layers dropdown menu
   * It formats an ArcGIS server REST query for the selected layer
   * Then it asks the attribute service to get all of the data for that layer
   * @param layer: AGSlayer object to select
   */
  public async selectLayer(layer) {
    try {
      if (layer) {
      const queryObject: QueryObject = {};
      queryObject.name = layer.name;
      queryObject.layerTaskUrl = layer.url + '/' + layer.visibleLayersArr[0];
      queryObject.relateObj = layer.relateObj;
      queryObject.outFields = layer.outFields;
      queryObject.formatFields = layer.formatFields;
      queryObject.queryMethod = 'POST';
      queryObject.returnGeometry = true;
      queryObject.token = layer.token;
      queryObject.returnType = 'OBJECTIDS';
      queryObject.whereClause = 'OBJECTID > 0';
      await this.getFullLayer(queryObject);
      }
      else {
        console.log('No layer in selectLayer in attribute service');
      }
    } catch (error) {
      this.ga.exception(error, false);
    }
  }

  /**
   * controls what happens when a query request is made. It grabs all of the objectids returned and queries the
   * feature class and all of the related tables for all of those objectIDs 1000 entries at a time
   * @param query: ArcGIS server query result
   */
  public async parseQuery(query: QueryObjectResults) {
    try {
      const resultArr = [];
      if (query.queryResults.objectIdArr && query.queryResults.objectIdArr.length > 0) {
        for (let i = 0; i < query.queryResults.objectIdArr.length; i += 1000) {
          let subset = [];
          const queryTaskObj: QueryObject = {};
          subset = query.queryResults.objectIdArr.slice(i, i + 1000);
          let objectids = '';
          for (const j of subset) {
            if (objectids === '') {
              objectids = objectids + j;
            } else {
              objectids = objectids + ',' + j;
            }
          }
          queryTaskObj.layerTaskUrl = query.queryTaskObj.layerTaskUrl;
          queryTaskObj.objectIds = objectids;
          queryTaskObj.returnGeometry = true;
          queryTaskObj.geometryPrecision = 4;
          queryTaskObj.returnType = 'FEATURES';
          queryTaskObj.queryMethod = 'POST';
          queryTaskObj.token = query.queryTaskObj.token;
          queryTaskObj.name = query.queryTaskObj.name;
          queryTaskObj.relateObj = query.queryTaskObj.relateObj;
          queryTaskObj.outFields = query.queryTaskObj.outFields;
          queryTaskObj.formatFields = query.queryTaskObj.formatFields;
          queryTaskObj.parent = query.queryTaskObj.parent;
          resultArr.push(this.query.queryData(queryTaskObj));
        }
      } else {
        return [query];
      }
      return await Promise.all(resultArr);
    } catch (error) {
      this.ga.exception(error, false);
      return [];
    }
  }
}
